/**
 * DUEL Program - Plan Screen
 *
 * Displays the generated weekly training plan.
 * Pro-gated (guard at setup prevents reaching here without Pro).
 *
 * Doctrine: AGENTS.md - no competitive engine changes, no migrations.
 */

import { useState, useEffect, useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useFocusEffect } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { loadCoachPlan, loadCoachProfile, saveCoachPlan, saveCoachProfile } from '@/lib/coachStorage';
import { generateWeekPlan } from '@/lib/coachEngine';
import type { CoachWeekPlan, CoachWorkoutDay } from '@/lib/coachEngine';
import {
  saveWorkoutCompletion, saveCheckInFeedback, loadCheckInFeedback,
  getVolumeAdjustment, type WorkoutFeedback,
} from '@/lib/coachCompletion';
import { Alert, Modal } from 'react-native';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, TRACKING, SPACE } from '@/constants/designTokens';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Play,
  RotateCcw,
  Settings,
  Zap,
} from 'lucide-react-native';

export default function DuelCoachPlanScreen() {
  const router = useRouter();
  const { theme } = useTheme();
  const { profile } = useAuth();
  const c = theme.colors;

  const [plan, setPlan] = useState<CoachWeekPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set([0]));
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [checkInDone, setCheckInDone] = useState(false);

  const loadPlan = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      const saved = await loadCoachPlan(profile.id);
      setPlan(saved);
      // Show weekly check-in if week > 1, not already done, and not dismissed this week
      if (saved && saved.weekNumber > 1) {
        const feedback = await loadCheckInFeedback(profile.id, saved.weekNumber - 1);
        if (feedback) {
          setCheckInDone(true);
        } else {
          // Only show once per week - check if user dismissed it already this week
          const dismissKey = `duel_coach_checkin_dismissed_${profile.id}_w${saved.weekNumber - 1}`;
          const dismissed = await AsyncStorage.getItem(dismissKey);
          if (!dismissed) setShowCheckIn(true);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  const handleCheckIn = async (feedback: WorkoutFeedback) => {
    if (!profile?.id || !plan) return;
    await saveCheckInFeedback(profile.id, plan.weekNumber - 1, feedback);
    setShowCheckIn(false);
    setCheckInDone(true);
    // Regenerate plan with volume adjustment
    const savedProfile = await loadCoachProfile(profile.id);
    if (!savedProfile) return;
    const adjustment = getVolumeAdjustment(feedback);
    const updatedProfile = { ...savedProfile, volumeAdjustment: adjustment };
    await saveCoachProfile(profile.id, updatedProfile);
    const newPlan = generateWeekPlan(updatedProfile);
    await saveCoachPlan(profile.id, newPlan);
    setPlan(newPlan);
  };

  useFocusEffect(useCallback(() => {
    loadPlan();
  }, [loadPlan]));

  const handleRegenerate = async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      const savedProfile = await loadCoachProfile(profile.id);
      if (!savedProfile) { router.replace('/duel-coach' as any); return; }
      const newPlan = generateWeekPlan(savedProfile);
      const { saveCoachPlan } = await import('@/lib/coachStorage');
      await saveCoachPlan(profile.id, newPlan);
      setPlan(newPlan);
      setExpandedDays(new Set([0]));
    } finally {
      setLoading(false);
    }
  };

  const handleLoadIntoWorkout = (day: CoachWorkoutDay) => {
    const exercises = day.exercises.map((ex) => ({
      name: ex.name,
      sets: ex.sets,
      reps: ex.reps,
      restSeconds: ex.restSeconds,
      rpe: ex.rpe,
      notes: ex.notes ?? '',
    }));
    router.push({
      pathname: '/(tabs)/record',
      params: {
        coachDayJson: JSON.stringify(exercises),
        view: 'log',
      },
    } as any);
  };

  const toggleDay = (index: number) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  if (loading) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
        <ActivityIndicator color={c.accent} style={{ marginTop: 60 }} />
      </SafeAreaView>
    );
  }

  if (!plan) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
        <View style={[styles.header, { borderBottomColor: c.border }]}>
          <TouchableOpacity onPress={() => router.back()} style={styles.backBtn} accessibilityRole="button" accessibilityLabel="Go back">
            <ArrowLeft size={22} color={c.text} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: c.text }]}>DUEL Program</Text>
          <View style={{ width: 40 }} />
        </View>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: SPACE.xl }}>
          <Text style={[{ color: c.textSecondary, fontFamily: 'Inter_400Regular', fontSize: FONT.base, textAlign: 'center' }]}>
            No plan found. Set up your profile to generate a plan.
          </Text>
          <TouchableOpacity
            style={[styles.actionBtn, { backgroundColor: c.accent, marginTop: SPACE.lg }]}
            onPress={() => router.replace('/duel-coach' as any)}
            accessibilityRole="button"
            accessibilityLabel="Set up profile"
          >
            <Text style={[styles.actionBtnText, { color: c.background }]}>SET UP PROFILE</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const isDeload = plan.weekNumber % 4 === 0;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
      {/* Weekly Check-in Modal */}
      <Modal visible={showCheckIn} transparent animationType="fade" onRequestClose={() => setShowCheckIn(false)}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.75)', justifyContent: 'center', alignItems: 'center', padding: 24 }}>
          <View style={{ backgroundColor: c.surface, borderRadius: RADIUS.xs, padding: 24, width: '100%', gap: 16, borderWidth: 1, borderColor: c.border }}>
            <Text style={{ color: c.accent, fontSize: 11, fontWeight: '700', letterSpacing: 2, textTransform: 'uppercase' }}>Weekly Check-in</Text>
            <Text style={{ color: c.text, fontSize: 18, fontWeight: '800' }}>How did last week feel?</Text>
            <Text style={{ color: c.textSecondary, fontSize: 14 }}>Your answer adjusts next week's volume automatically.</Text>
            {([
              { key: 'too_easy' as WorkoutFeedback, label: 'Too Easy', sub: 'Increase volume next week', icon: '\uD83D\uDCAA' },
              { key: 'just_right' as WorkoutFeedback, label: 'Just Right', sub: 'Keep the same progression', icon: '\u2705' },
              { key: 'too_hard' as WorkoutFeedback, label: 'Too Hard', sub: 'Back off volume next week', icon: '\uD83D\uDE05' },
            ] as const).map((opt) => (
              <TouchableOpacity
                key={opt.key}
                style={{ backgroundColor: c.surfaceVariant, borderRadius: RADIUS.xs, padding: 16, borderWidth: 1, borderColor: c.border }}
                onPress={() => handleCheckIn(opt.key)}
                accessibilityRole="button"
              >
                <Text style={{ color: c.text, fontSize: 15, fontWeight: '700' }}>{opt.icon} {opt.label}</Text>
                <Text style={{ color: c.textSecondary, fontSize: 12, marginTop: 2 }}>{opt.sub}</Text>
              </TouchableOpacity>
            ))}
            <TouchableOpacity onPress={async () => {
              setShowCheckIn(false);
              if (profile?.id && plan) {
                const dismissKey = `duel_coach_checkin_dismissed_${profile.id}_w${plan.weekNumber - 1}`;
                await AsyncStorage.setItem(dismissKey, 'true');
              }
            }}>
              <Text style={{ color: c.textTertiary, fontSize: 13, textAlign: 'center' }}>Skip for now</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Header */}
      <View style={[styles.header, { borderBottomColor: c.border }]}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backBtn} accessibilityRole="button" accessibilityLabel="Go back">
          <ArrowLeft size={22} color={c.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: c.text }]}>WEEK {plan.weekNumber}</Text>
        <TouchableOpacity
          onPress={() => router.push('/duel-coach' as any)}
          style={styles.settingsBtn}
          accessibilityRole="button"
          accessibilityLabel="Update profile"
        >
          <Settings size={20} color={c.textSecondary} />
        </TouchableOpacity>
      </View>

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={styles.container}
        showsVerticalScrollIndicator={false}
      >
        {/* Split label */}
        <View style={styles.splitRow}>
          <Text style={[styles.splitLabel, { color: c.accent }]}>{plan.split.toUpperCase()}</Text>
          {isDeload && (
            <View style={[styles.deloadBadge, { backgroundColor: '#F59E0B20', borderColor: '#F59E0B40' }]}>
              <Text style={[styles.deloadBadgeText, { color: '#F59E0B' }]}>DELOAD</Text>
            </View>
          )}
        </View>

        {/* SMART Goal - ACSM Table 12.4 */}
        {plan.smartGoal && (
          <View style={[styles.adaptationCard, { backgroundColor: c.surfaceVariant, borderColor: c.border }]}>
            <Text style={{ fontSize: 10, fontWeight: '800', letterSpacing: 1.5, color: c.textTertiary, textTransform: 'uppercase', marginBottom: 4 }}>YOUR GOAL</Text>
            <Text style={[styles.adaptationNote, { color: c.text }]}>{plan.smartGoal}</Text>
          </View>
        )}

        {/* Adaptation note */}
        <View style={[styles.adaptationCard, { backgroundColor: c.surface, borderColor: c.accent + '30' }]}>
          <View style={styles.adaptationHeader}>
            <Zap size={14} color={c.accent} />
            <Text style={[styles.adaptationTitle, { color: c.accent }]}>THIS WEEK</Text>
          </View>
          <Text style={[styles.adaptationNote, { color: c.textSecondary }]}>{plan.adaptationNote}</Text>
        </View>

        {/* Barrier tip - ACSM Table 12.3 evidence-based strategies */}
        {plan.barrierTip && (
          <View style={[styles.adaptationCard, { backgroundColor: c.surfaceVariant, borderColor: c.border }]}>
            <Text style={{ fontSize: 10, fontWeight: '800', letterSpacing: 1.5, color: c.textTertiary, textTransform: 'uppercase', marginBottom: 4 }}>COACH NOTE</Text>
            <Text style={[styles.adaptationNote, { color: c.textSecondary }]}>{plan.barrierTip}</Text>
          </View>
        )}

        {/* Days */}
        {plan.days.map((day, index) => (
          <DayCard
            key={index}
            day={day}
            index={index}
            expanded={expandedDays.has(index)}
            onToggle={() => toggleDay(index)}
            onLoad={() => handleLoadIntoWorkout(day)}
            colors={c}
          />
        ))}

        {/* Actions */}
        <View style={styles.actionsRow}>
          <TouchableOpacity
            style={[styles.actionBtn, { backgroundColor: c.surface, borderColor: c.border, borderWidth: 1, flex: 1 }]}
            onPress={handleRegenerate}
            accessibilityRole="button"
            accessibilityLabel="Regenerate plan"
          >
            <RotateCcw size={16} color={c.textSecondary} />
            <Text style={[styles.actionBtnText, { color: c.textSecondary }]}>REGENERATE</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.actionBtn, { backgroundColor: c.surface, borderColor: c.border, borderWidth: 1, flex: 1 }]}
            onPress={() => router.push('/duel-coach' as any)}
            accessibilityRole="button"
            accessibilityLabel="Update profile"
          >
            <Settings size={16} color={c.textSecondary} />
            <Text style={[styles.actionBtnText, { color: c.textSecondary }]}>UPDATE PROFILE</Text>
          </TouchableOpacity>
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

// -----------------------------------------------------------------------------
// Day card
// -----------------------------------------------------------------------------

type Colors = ReturnType<typeof useTheme>['theme']['colors'];

function DayCard({
  day,
  index,
  expanded,
  onToggle,
  onLoad,
  colors: c,
}: {
  day: CoachWorkoutDay;
  index: number;
  expanded: boolean;
  onToggle: () => void;
  onLoad: () => void;
  colors: Colors;
}) {
  return (
    <View style={[dayStyles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
      {/* Day header - always visible */}
      <TouchableOpacity
        style={dayStyles.header}
        onPress={onToggle}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={`${day.dayLabel}, ${expanded ? 'collapse' : 'expand'}`}
      >
        <View style={{ flex: 1 }}>
          <Text style={[dayStyles.dayLabel, { color: c.text }]}>{day.dayLabel.toUpperCase()}</Text>
          <Text style={[dayStyles.focus, { color: c.textSecondary }]}>{day.focus}</Text>
          <Text style={[dayStyles.meta, { color: c.textTertiary }]}>
            {day.exercises.length}{' exercises \u00B7 ~'}{day.estimatedMinutes}{' min'}
          </Text>
        </View>
        {expanded ? (
          <ChevronUp size={18} color={c.textTertiary} />
        ) : (
          <ChevronDown size={18} color={c.textTertiary} />
        )}
      </TouchableOpacity>

      {/* Expanded exercise list */}
      {expanded && (
        <>
          <View style={[dayStyles.divider, { backgroundColor: c.border }]} />
          {day.exercises.map((ex, i) => (
            <View key={i} style={[dayStyles.exerciseRow, i > 0 ? { borderTopWidth: 1, borderTopColor: c.border + '60' } : {}]}>
              <View style={{ flex: 1 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <Text style={[dayStyles.exerciseName, { color: c.text }]}>{ex.name}</Text>
                  {ex.exerciseType === 'core' && (
                    <View style={{ backgroundColor: c.accent + '20', borderRadius: 2, paddingHorizontal: 4, paddingVertical: 1 }}>
                      <Text style={{ fontSize: 9, fontWeight: '800', color: c.accent, letterSpacing: 0.5 }}>COMPOUND</Text>
                    </View>
                  )}
                  {ex.supersetGroup != null && (
                    <View style={{ backgroundColor: '#8B5CF620', borderRadius: 2, paddingHorizontal: 4, paddingVertical: 1 }}>
                      <Text style={{ fontSize: 9, fontWeight: '800', color: '#8B5CF6', letterSpacing: 0.5 }}>SUPERSET {ex.supersetGroup + 1}</Text>
                    </View>
                  )}
                </View>
                <View style={dayStyles.exerciseMeta}>
                  <Text style={[dayStyles.exerciseDetail, { color: c.accent }]}>
                    {ex.sets}{' \u00D7 '}{ex.reps}
                  </Text>
                  {ex.rpe != null && (
                    <Text style={[dayStyles.exerciseDetail, { color: c.textTertiary }]}>
                      {'\u00B7 RPE '}{ex.rpe}
                    </Text>
                  )}
                  <Text style={[dayStyles.exerciseDetail, { color: c.textTertiary }]}>
                    {'\u00B7 '}{ex.restSeconds >= 60 ? `${Math.floor(ex.restSeconds / 60)}m` : `${ex.restSeconds}s`}{' rest'}
                  </Text>
                </View>
                {ex.notes ? (
                  <Text style={[dayStyles.exerciseNotes, { color: c.textTertiary }]}>{ex.notes}</Text>
                ) : null}
              </View>
            </View>
          ))}

          {/* Load into workout */}
          <View style={[dayStyles.divider, { backgroundColor: c.border }]} />
          <TouchableOpacity
            style={[dayStyles.loadBtn, { backgroundColor: c.accent + '18', borderColor: c.accent + '40' }]}
            onPress={onLoad}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel={`Load ${day.dayLabel} into workout`}
          >
            <Play size={14} color={c.accent} />
            <Text style={[dayStyles.loadBtnText, { color: c.accent }]}>LOAD INTO WORKOUT</Text>
          </TouchableOpacity>
        </>
      )}
    </View>
  );
}

const dayStyles = StyleSheet.create({
  card: { borderWidth: 1, borderRadius: RADIUS.xs, marginBottom: SPACE.sm, overflow: 'hidden' },
  header: { flexDirection: 'row', alignItems: 'center', padding: SPACE.base, gap: SPACE.sm },
  dayLabel: { fontSize: FONT.sm, fontFamily: 'BarlowCondensed_700Bold', letterSpacing: TRACKING.tight },
  focus: { fontSize: FONT.xs, fontFamily: 'Inter_400Regular', marginTop: 2 },
  meta: { fontSize: FONT.xs, fontFamily: 'Inter_400Regular', marginTop: 2 },
  divider: { height: 1 },
  exerciseRow: { paddingHorizontal: SPACE.base, paddingVertical: SPACE.sm },
  exerciseName: { fontSize: FONT.base, fontFamily: 'Inter_600SemiBold' },
  exerciseMeta: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 4, marginTop: 2 },
  exerciseDetail: { fontSize: FONT.xs, fontFamily: 'Inter_500Medium' },
  exerciseNotes: { fontSize: FONT.xs, fontFamily: 'Inter_400Regular', marginTop: 4, fontStyle: 'italic' },
  loadBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACE.xs,
    margin: SPACE.sm,
    paddingVertical: SPACE.sm,
    borderRadius: RADIUS.xs,
    borderWidth: 1,
  },
  loadBtnText: { fontSize: FONT.sm, fontFamily: 'BarlowCondensed_700Bold', letterSpacing: TRACKING.tight },
});

// -----------------------------------------------------------------------------
// Main styles
// -----------------------------------------------------------------------------

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: SPACE.lg,
    paddingVertical: SPACE.base,
    borderBottomWidth: 1,
  },
  headerTitle: { fontSize: FONT.lg, fontFamily: 'Oswald_700Bold', letterSpacing: TRACKING.wide },
  backBtn: { width: 40, alignItems: 'flex-start' },
  settingsBtn: { width: 40, alignItems: 'flex-end' },
  container: { paddingHorizontal: SPACE.lg, paddingTop: SPACE.md, paddingBottom: 64, gap: SPACE.sm },
  splitRow: { flexDirection: 'row', alignItems: 'center', gap: SPACE.sm, marginBottom: SPACE.xs },
  splitLabel: { fontSize: FONT.xl, fontFamily: 'Oswald_700Bold', letterSpacing: TRACKING.wide },
  deloadBadge: { paddingHorizontal: SPACE.sm, paddingVertical: 2, borderRadius: RADIUS.xs, borderWidth: 1 },
  deloadBadgeText: { fontSize: FONT.xs, fontFamily: 'BarlowCondensed_700Bold', letterSpacing: TRACKING.tight },
  adaptationCard: {
    borderWidth: 1,
    borderRadius: RADIUS.xs,
    padding: SPACE.base,
    gap: SPACE.xs,
    marginBottom: SPACE.sm,
  },
  adaptationHeader: { flexDirection: 'row', alignItems: 'center', gap: SPACE.xs },
  adaptationTitle: { fontSize: FONT.xs, fontFamily: 'BarlowCondensed_700Bold', letterSpacing: TRACKING.wide },
  adaptationNote: { fontSize: FONT.sm, fontFamily: 'Inter_400Regular', lineHeight: 20 },
  actionsRow: { flexDirection: 'row', gap: SPACE.sm, marginTop: SPACE.sm },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACE.xs,
    paddingVertical: SPACE.sm,
    paddingHorizontal: SPACE.base,
    borderRadius: RADIUS.xs,
  },
  actionBtnText: { fontSize: FONT.xs, fontFamily: 'BarlowCondensed_700Bold', letterSpacing: TRACKING.tight },
});

